import { useState } from "react";
import { authAxios } from "@/utils/auth";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Eye, Unlock, EyeOff } from "lucide-react";
import { toast } from "sonner";

// Privacy level labels
const PRIVACY_LEVELS = {
  0: "Always (Practically Public Knowledge)",
  1: "L1: Spark",
  2: "L2: Opening",
  3: "L3: Resonance",
  4: "L4: Safety",
  5: "L5: Curiosity",
  6: "L6: Attraction",
  7: "L7: Vulnerability",
  8: "L8: Alignment",
  9: "L9: Momentum",
  10: "L10: Intimacy",
  11: "L11: Integration",
  12: "L12: Commitment",
  13: "L13: Union",
  14: "Never (Only used for Agent Advice)",
};

// Privacy level color helper
export const getPrivacyColor = (level) => {
  if (level === 0) return "text-green-400";
  if (level <= 4) return "text-blue-400";
  if (level <= 9) return "text-amber-400";
  if (level <= 13) return "text-orange-400";
  return "text-red-400"; // Level 14 (Never)
};

// Get icon component based on level
export const getPrivacyIconElement = (level) => {
  if (level === 0) return <Unlock className="w-3.5 h-3.5" />;
  if (level === 14) return <EyeOff className="w-3.5 h-3.5" />;
  return <Eye className="w-3.5 h-3.5" />;
};

// Privacy Icon Button Component
export function PrivacyIcon({ 
  fieldKey, 
  privacyLevel = 0, 
  matchId,
  matchName,
  onPrivacyChange,
  size = "sm"
}) {
  const { user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(privacyLevel);
  const [saving, setSaving] = useState(false);

  // Privacy-level lock/unlock affordance is admin-only. Non-admins never see
  // the "L{level}" pill or the modal it opens; the underlying setting is
  // still applied server-side, just not user-editable from this surface.
  if (user?.role !== "admin") {
    return null;
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.patch(`/privacy-settings/${matchId}/field`, {
        field_key: fieldKey,
        privacy_level: selectedLevel,
      });
      
      if (onPrivacyChange) {
        onPrivacyChange(fieldKey, selectedLevel);
      }
      
      toast.success("Privacy level updated");
      setModalOpen(false);
    } catch (error) {
      console.error("Error updating privacy:", error);
      toast.error("Failed to update privacy level");
    } finally {
      setSaving(false);
    }
  };

  const formatFieldName = (key) => {
    return key?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "";
  };

  const buttonSizeClass = size === "sm" ? "h-5 px-1" : "h-6 px-1.5";
  const iconSizeClass = size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5";
  const textSizeClass = size === "sm" ? "text-[10px]" : "text-xs";

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={(e) => {
          e.stopPropagation();
          setSelectedLevel(privacyLevel);
          setModalOpen(true);
        }}
        className={`${buttonSizeClass} ${getPrivacyColor(privacyLevel)} hover:bg-white/10 flex items-center gap-0.5`}
        title={`Privacy: ${PRIVACY_LEVELS[privacyLevel] || `Level ${privacyLevel}`}`}
        data-testid={`privacy-icon-${fieldKey}`}
      >
        {privacyLevel === 0 ? (
          <Unlock className={iconSizeClass} />
        ) : privacyLevel === 14 ? (
          <EyeOff className={iconSizeClass} />
        ) : (
          <Eye className={iconSizeClass} />
        )}
        <span className={`${textSizeClass} font-medium`}>L{privacyLevel}</span>
      </Button>

      {/* Privacy Level Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-amber-400" />
              Privacy Level
            </DialogTitle>
            <DialogDescription className="text-white/60">
              When does <span className="text-white font-medium">{matchName || "this persona"}</span> feel comfortable sharing this information?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="text-sm text-white/70">
              Field: <span className="text-white font-medium">
                {formatFieldName(fieldKey)}
              </span>
            </div>
            
            <Select
              value={String(selectedLevel)}
              onValueChange={(val) => setSelectedLevel(parseInt(val))}
            >
              <SelectTrigger className="bg-white/5 border-white/20 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10 max-h-[300px]">
                {Object.entries(PRIVACY_LEVELS).map(([level, label]) => (
                  <SelectItem 
                    key={level} 
                    value={level}
                    className="text-white hover:bg-white/10"
                  >
                    <span className="flex items-center gap-2">
                      <span className={`w-6 text-center ${getPrivacyColor(parseInt(level))}`}>
                        {level}
                      </span>
                      <span>{label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setModalOpen(false)}
                className="border-white/20 text-white/70"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-amber-500 text-black hover:bg-amber-400"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export { PRIVACY_LEVELS };
export default PrivacyIcon;
